import { useEffect, useState } from 'react';

import { useKioskSession } from '@/lib/kioskSession';
import { fetchOpenings } from '@/services/openings';

import type { JobList } from '../types';

export interface OpeningsState {
  readonly loading: boolean;
  readonly list: JobList | undefined;
  /** True when the request itself failed, which is NOT the same as an empty pool — see `types.ts`. */
  readonly unreachable: boolean;
}

/**
 * Fetches the openings once, when the list screen mounts.
 *
 * **Once, never a poll** [decision 0029]. A candidate standing at the device does not benefit from the list
 * changing under their finger, and KJM-2's freeze rule says a mid-session republish must not move the pool
 * for someone already in flight — a re-fetch would be exactly that.
 *
 * The result is written into the session store as well as returned, because the detail screen reads the
 * selected order from the store rather than re-fetching it: the published description already arrived here.
 */
export function useOpenings(): OpeningsState {
  /*
   * No ZIP is sent, and there is nowhere to get one. Check-in collects four identity factors and none is an
   * address; KAP-7's quick-apply, where ZIP is field 7, is not built. The BACKEND reads it from the matched
   * candidate's Bullhorn record instead — the only place it exists — so all this has to send is who they are.
   *
   * ⭐ **And no venue either, since 03-us-kad-6c.** This hook named `PILOT_VENUE_ID`, the pilot's first site
   * hard-coded in `lib/venue.ts` against the day the PIN screen existed. It exists: the tablet enrols
   * against a venue's PIN and the venue rides on the credential [decisions 0204, 0221].
   */
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const storedList = useKioskSession((state) => state.jobs);
  const setJobs = useKioskSession((state) => state.setJobs);

  const [unreachable, setUnreachable] = useState(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (storedList !== undefined) return;

    let live = true;

    void fetchOpenings({ candidateId }).then((outcome) => {
      if (!live) return;
      if (outcome.kind === 'list') {
        setJobs(outcome.list);
      } else {
        setUnreachable(true);
      }
      setSettled(true);
    });

    // A candidate who walks away mid-request ends the session, which unmounts this. Without the guard the
    // resolved promise writes a list into the NEXT candidate's store.
    return () => {
      live = false;
    };
  }, [storedList, candidateId, setJobs]);

  /*
   * Derived rather than held in state. An earlier version called `setLoading(true)` in the effect body,
   * which `react-hooks/set-state-in-effect` flags for a real reason: it schedules a second render before the
   * first has painted. Deriving it also makes the "returning from the detail screen" case correct for free —
   * the store already holds a list, so there is no spinner flash over a list that is already known.
   */
  const loading = storedList === undefined && !unreachable && !settled;

  return { loading, list: storedList, unreachable };
}
